import {
  ArrowLeft,
  Braces,
  BrainCircuit,
  Check,
  ChevronRight,
  FileClock,
  FileText,
  Gauge,
  Loader2,
  Maximize2,
  MessageSquareText,
  Minimize2,
  Pencil,
  Plus,
  Send,
  Settings2,
  Sparkles,
  Wrench,
  Navigation,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { core } from "../../lib/api";
import { useToast } from "../../components/ui/toast";
import { CopyButton } from "../../components/ui/copy-button";
import { cn } from "../../lib/utils";
import { useDiscardWarning } from "../../hooks/use-discard-warning";
import { useAuth } from "../../context/AuthContext";
import { canAccessKnowledge, getBooleanSetting, isAdminUser } from "../../lib/userAccess";
import {
  extractPromptVariables,
  findMalformedPromptVariables,
  getPromptMentionOptions,
  isBuiltInPromptVariable,
  promptUsesCalendarVariables,
} from "../../lib/promptVariables";
import { STT_LANGUAGE_GROUPS } from "../../lib/sttLanguages";
import {
  DEFAULT_SWITCH_PERCENT,
  defaultLanguagePreference,
  EMPTY_LANGUAGE_PREFERENCE,
  languagePreferenceProblem,
  preferenceAfterSttChange,
  readReplyLanguage,
  type LanguagePreference,
} from "../../lib/languagePreference";
import { normalizeInfieldLanguageOptions } from "../../lib/infieldLanguages";
import { AgentTestWorkspace } from "./agent-test-workspace";
import {
  ExternalPromptEditor,
  type ExternalPromptResponse,
} from "./external-prompt-editor";
import type { Agent } from "../home/types";
import {
  PublishVersionModal,
  type PublishMode,
} from "./publish-version-modal";
import {
  LanguagePreferenceFields,
  PostCallFieldEditor,
  PromptTokenMenu,
  SentenceLibrary,
  TransferEditor,
  VersionHistory,
} from "./studio-editors";
import { OutcomeFindingsPanel } from "./outcome-findings";
import type { OutcomeSections } from "./outcome-sections";
import { useOutcomeGate } from "./outcome-gate";
import { findingsFromError, type OutcomeOverride } from "./outcome-validation";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { ScoutBuilderPane } from "../ask/scout-builder-pane";
import type { ScoutConversation } from "../ask/scout-api";
import { PromptMarkdown } from "./prompt-markdown";
import { KnowledgeEditor } from "../knowledge/knowledge-editor";
import type { AgentKnowledgeConfig } from "../knowledge/api";
import { ScoutButton } from "../../components/ui/scout-button";

type StudioSection = "prompt" | "reasoning" | "content" | "tools" | "call-data" | "runtime" | "versions";

interface ValidValue {
  value: string;
  description?: string;
}

interface CallField {
  fieldName: string;
  fieldType?: string;
  description?: string;
  // The four boxes of a new outcome, and the free-text flag (see outcome-sections.ts).
  sections?: Partial<OutcomeSections> | null;
  freeText?: boolean;
  acceptedFindings?: Array<{ rule_id: string; reason: string }>;
  visible?: boolean;
  required?: boolean;
  normalise?: boolean;
  emphasis?: boolean;
  to_lowercase?: boolean;
  number_to_words?: boolean;
  infield_language?: string;
  validValues?: ValidValue[];
  defaultValue?: string;
  retryValues?: string[];
  successValues?: string[];
  exclusive_group?: string;
  outcome_priority?: number | null;
  outcome_single_winner?: boolean;
  is_primary?: boolean;
}

interface FunctionTool {
  function_tool_id?: string;
  name?: string;
  actual_name?: string;
  keyword?: string;
}

interface AgentVersion {
  version_id: string;
  version_name?: string;
  system_prompt?: string;
  first_message?: string;
  call_infields?: CallField[];
  call_outcomes?: CallField[];
  outcome_single_winner?: boolean;
  tool?: FunctionTool[];
  stt_language?: string;
  thinker_cases?: string[];
  talker_cases?: string[];
  sentence_catalogue?: SentenceCatalogueItem[];
  language_switch?: boolean;
  primary_language?: string | null;
  secondary_language?: string | null;
  language_switch_percent?: number | null;
  transfer?: TransferConfig | null;
  status?: string;
  published_at?: string;
  created_by?: string;
  is_summary?: boolean;
  /** fieldName of the primary/headline outcome for this version's KPI. */
  primary_outcome?: string | null;
}

interface SentenceCatalogueItem {
  name: string;
  sentence: string;
}

interface TransferConfig {
  enabled: boolean;
  destinations: Record<string, string>;
  on_time_limit_enabled: boolean;
  on_time_limit_label: string;
  on_time_limit_message: string;
  after_questions_enabled: boolean;
  after_questions_count: number;
  after_questions_label: string;
  after_questions_message: string;
}

interface AgentDocument extends Agent {
  status?: string;
  current_version?: string;
  version_count?: number;
  versions?: AgentVersion[];
  knowledge?: AgentKnowledgeConfig | null;
}

interface ToolOption extends FunctionTool {
  description?: string;
}

interface AutoBuilderStudioDraft {
  session_id: string;
  builder_type: "single";
  agent_name: string;
  description: string;
  version_name: string;
  prompt: {
    system_prompt?: string;
    first_message?: string;
    call_infields?: CallField[];
    call_outcomes?: CallField[];
    outcome_single_winner?: boolean;
    tool?: FunctionTool[];
    sentence_catalogue?: SentenceCatalogueItem[];
    stt_language?: string;
    language_switch?: boolean;
    primary_language?: string | null;
    secondary_language?: string | null;
    language_switch_percent?: number | null;
    transfer?: TransferConfig | null;
  };
}

interface AgentDraft {
  agentName: string;
  description: string;
  versionName: string;
  systemPrompt: string;
  firstMessage: string;
  infields: CallField[];
  outcomes: CallField[];
  outcomeSingleWinner: boolean;
  tools: FunctionTool[];
  sttLanguage: string;
  languageSwitch: boolean;
  languagePreference: LanguagePreference;
  sentenceCatalogue: SentenceCatalogueItem[];
  transfer: TransferConfig | null;
  thinkerCases: string;
  talkerCases: string;
  /** fieldName of the outcome the user marked as the primary KPI (empty = none). */
  primaryOutcome: string;
}

function readLanguagePreference(source: {
  primary_language?: string | null;
  secondary_language?: string | null;
  language_switch_percent?: number | null;
}): LanguagePreference {
  return {
    primaryLanguage: readReplyLanguage(source.primary_language),
    secondaryLanguage: readReplyLanguage(source.secondary_language),
    languageSwitchPercent: typeof source.language_switch_percent === "number" ? source.language_switch_percent : null,
  };
}

const EMPTY_DRAFT: AgentDraft = {
  agentName: "",
  description: "",
  versionName: "Initial draft",
  systemPrompt: "",
  firstMessage: "",
  infields: [],
  outcomes: [],
  outcomeSingleWinner: false,
  tools: [],
  sttLanguage: "hi",
  languageSwitch: false,
  languagePreference: EMPTY_LANGUAGE_PREFERENCE,
  sentenceCatalogue: [],
  transfer: null,
  thinkerCases: "",
  talkerCases: "",
  primaryOutcome: "",
};

const NEW_AGENT_DRAFT: AgentDraft = {
  ...EMPTY_DRAFT,
  agentName: "Untitled Agent",
};

const singleAgentSections: Array<{
  id: StudioSection;
  label: string;
  icon: typeof FileText;
}> = [
  { id: "prompt", label: "Agent details", icon: FileText },
  { id: "content", label: "Prompt library", icon: MessageSquareText },
  { id: "tools", label: "Functions", icon: Wrench },
  { id: "call-data", label: "Post-call data", icon: Braces },
  { id: "runtime", label: "Speech & fallback", icon: Settings2 },
  { id: "versions", label: "Version history", icon: FileClock },
];

// In external mode the client edits a template, not the prompt — and the
// rest of the studio is the same category of thing the template exists to
// keep out of their hands (tool schemas, post-call field config, ASR
// tuning). One tab, plus the test workspace beside it.
const externalPromptSections: typeof singleAgentSections = [
  { id: "prompt", label: "Agent prompt", icon: FileText },
];

const thinkerTalkerSections: typeof singleAgentSections = [
  { id: "prompt", label: "Agent details", icon: FileText },
  { id: "reasoning", label: "Reasoning routes", icon: BrainCircuit },
  { id: "content", label: "Prompt library", icon: MessageSquareText },
  { id: "tools", label: "Functions", icon: Wrench },
  { id: "call-data", label: "Post-call data", icon: Braces },
  { id: "runtime", label: "Speech & fallback", icon: Settings2 },
  { id: "versions", label: "Version history", icon: FileClock },
];

function normalizeVersion(version: AgentVersion, agent: AgentDocument): AgentDraft {
  return {
    agentName: agent.agent_name || agent.name || "Untitled Agent",
    description: agent.description || "",
    versionName: version.version_name || "Draft",
    systemPrompt: version.system_prompt || "",
    firstMessage: version.first_message || "",
    infields: Array.isArray(version.call_infields) ? version.call_infields : [],
    outcomes: Array.isArray(version.call_outcomes) ? version.call_outcomes : [],
    outcomeSingleWinner: version.outcome_single_winner === true,
    tools: Array.isArray(version.tool) ? version.tool : [],
    sttLanguage: version.stt_language || "hi",
    languageSwitch: version.language_switch === true,
    languagePreference: readLanguagePreference(version),
    sentenceCatalogue: Array.isArray(version.sentence_catalogue) ? version.sentence_catalogue : [],
    transfer: version.transfer ?? null,
    thinkerCases: (version.thinker_cases || []).join("\n"),
    talkerCases: (version.talker_cases || []).join("\n"),
    primaryOutcome: typeof version.primary_outcome === "string" && version.primary_outcome
      ? version.primary_outcome
      : (version.call_outcomes || []).find((o) => o.is_primary)?.fieldName || "",
  };
}

function fieldKey(field: CallField, index: number) {
  return `${field.fieldName || "field"}-${index}`;
}

// lookup_date stores its scheduling window in the tool's `keyword` field as
// "HH:MM-HH:MM" (the same single-string field the nearby-places tool uses for
// its keyword), so the value rides the existing dashboard → core → worker
// plumbing with no extra schema.
const DEFAULT_SCHEDULING_WINDOW = "08:00-22:00";

function splitSchedulingWindow(value?: string): [string, string] {
  const match = String(value || "").match(/(\d{1,2}:\d{2})\s*(?:-|to|–)\s*(\d{1,2}:\d{2})/i);
  if (!match) {
    const [fallbackStart, fallbackEnd] = DEFAULT_SCHEDULING_WINDOW.split("-");
    return [fallbackStart, fallbackEnd];
  }
  return [match[1], match[2]];
}

function joinSchedulingWindow(start: string, end: string) {
  return `${start || "08:00"}-${end || "22:00"}`;
}

export function SingleAgentStudio() {
  const navigate = useNavigate();
  const toast = useToast();
  const outcomeGate = useOutcomeGate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get("agent_id") || searchParams.get("agentId") || "";
  const autoBuilderSessionId = searchParams.get("autobuilder_session_id") || "";
  const scoutConversationId = searchParams.get("scout_conversation_id") || "";
  const scoutSessionId = searchParams.get("scout_session_id") || "";

  const requestedTab = searchParams.get("tab");
  const [section, setSection] = useState<StudioSection>(
    requestedTab === "version" ? "versions" : "prompt",
  );
  const [agent, setAgent] = useState<AgentDocument | null>(null);
  // The external prompt decides which editor this studio shows at all, so it
  // is loaded here rather than inside the panel — the tab list depends on it.
  const [externalPrompt, setExternalPrompt] = useState<ExternalPromptResponse | null>(null);
  const [externalPromptLoading, setExternalPromptLoading] = useState(false);
  const [externalPromptReloads, setExternalPromptReloads] = useState(0);
  // Auto-poll budget for a pending template: 30 rounds × 3s ≈ 90s. Past that
  // the editor swaps the spinner for a leave-and-return panel (pollGaveUp)
  // instead of polling forever against a dead worker.
  const externalPromptPolls = useRef(0);
  const [externalPromptPollGaveUp, setExternalPromptPollGaveUp] = useState(false);
  const [externalPromptDirty, setExternalPromptDirty] = useState(false);
  const [externalPromptExpanded, setExternalPromptExpanded] = useState(false);
  const [savingMetadata, setSavingMetadata] = useState(false);
  const externalAdmin = { can: isAdminUser(user) };

  const externalMode = getBooleanSetting(user, 'external_prompt_feature', false) && Boolean(user?.agent_create_feature !== false);
  const isAutoBuilderDraft = Boolean((autoBuilderSessionId || (externalMode && scoutSessionId)) && !agentId);
  const isNew = (searchParams.get("new") === "1" || isAutoBuilderDraft) && !agentId;
  const requestedArchitecture = !isAutoBuilderDraft && searchParams.get("architecture") === "thinker_talker"
    ? "thinker_talker"
    : "single";
  const showExternalWorkspace = externalMode || (externalAdmin.can && externalPrompt?.enabled === true);
  const [externalPromptError, setExternalPromptError] = useState("");
  const [versions, setVersions] = useState<AgentVersion[]>([]);
  const [activeVersionId, setActiveVersionId] = useState("");
  const [draft, setDraft] = useState<AgentDraft>(EMPTY_DRAFT);
  const [toolOptions, setToolOptions] = useState<ToolOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [toolsLoaded, setToolsLoaded] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishMode, setPublishMode] = useState<PublishMode>("new_version");
  const [newVersionName, setNewVersionName] = useState("");
  const [overwriteVersionName, setOverwriteVersionName] = useState("");
  const [editingSystemPrompt, setEditingSystemPrompt] = useState(false);
  const [normalPromptExpanded, setNormalPromptExpanded] = useState(false);
  const [hasPendingSchedule, setHasPendingSchedule] = useState(false);
  const [mentionMenu, setMentionMenu] = useState<{
    field: "systemPrompt" | "firstMessage" | null;
    cursorIndex: number;
    selectedIndex: number;
  }>({ field: null, cursorIndex: 0, selectedIndex: 0 });
  const [scoutOpen, setScoutOpen] = useState(Boolean(scoutConversationId) && (!externalMode || isNew));
  const [scoutAppliedSession, setScoutAppliedSession] = useState("");
  const [beforeScout, setBeforeScout] = useState<AgentDraft | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [compactStudio, setCompactStudio] = useState(() => window.innerWidth < 1280);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 1279px)");
    const update = () => setCompactStudio(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const systemPromptRef = useRef<HTMLTextAreaElement>(null);
  const firstMessageRef = useRef<HTMLInputElement>(null);
  const baselineDraft = useRef<AgentDraft>(NEW_AGENT_DRAFT);
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(baselineDraft.current), [draft]);
  const metadataDirty = useMemo(
    () => draft.agentName !== baselineDraft.current.agentName
      || draft.description !== baselineDraft.current.description,
    [draft.agentName, draft.description],
  );
  const loadedAgentId = useRef("");
  const versionsRef = useRef<AgentVersion[]>([]);
  const versionsLoadedRef = useRef(false);
  const versionsRequestRef = useRef<Promise<AgentVersion[]> | null>(null);
  const infieldLanguageOptions = useMemo(
    () => normalizeInfieldLanguageOptions(user?.infield_languages),
    [user?.infield_languages],
  );

  const load = useCallback(async () => {
    if (isNew) {
      if (isAutoBuilderDraft) {
        const studioDraft = await core<AutoBuilderStudioDraft>(
          `/api/v1/prompt-builder/sessions/${encodeURIComponent(autoBuilderSessionId || scoutSessionId)}/studio-draft`,
        );
        const prompt = studioDraft.prompt || {};
        const nextDraft: AgentDraft = {
          ...NEW_AGENT_DRAFT,
          agentName: studioDraft.agent_name || NEW_AGENT_DRAFT.agentName,
          description: studioDraft.description || "",
          versionName: studioDraft.version_name || "AutoBuilder v1",
          systemPrompt: prompt.system_prompt || "",
          firstMessage: prompt.first_message || "",
          infields: Array.isArray(prompt.call_infields) ? prompt.call_infields : [],
          outcomes: Array.isArray(prompt.call_outcomes) ? prompt.call_outcomes : [],
          outcomeSingleWinner: prompt.outcome_single_winner === true,
          tools: Array.isArray(prompt.tool) ? prompt.tool : [],
          sttLanguage: prompt.stt_language || "en",
          languageSwitch: prompt.language_switch === true,
          languagePreference: readLanguagePreference(prompt),
          sentenceCatalogue: Array.isArray(prompt.sentence_catalogue)
            ? prompt.sentence_catalogue
            : [],
          transfer: prompt.transfer ?? null,
        };
        setAgent({
          agent_id: "",
          agent_name: nextDraft.agentName,
          description: nextDraft.description,
          folder_id: null,
          agent_type: "single",
          status: "Unpublished",
        });
        setVersions([]);
        versionsRef.current = [];
        versionsLoadedRef.current = true;
        setActiveVersionId("");
        setDraft(nextDraft);
        baselineDraft.current = nextDraft;
        return;
      }
      const initialDraft = {
        ...NEW_AGENT_DRAFT,
        agentName: requestedArchitecture === "thinker_talker"
          ? "Untitled Thinker-Talker"
          : NEW_AGENT_DRAFT.agentName,
      };
      setAgent({
        agent_id: "",
        agent_name: initialDraft.agentName,
        description: "",
        folder_id: null,
        agent_type: requestedArchitecture,
        status: "Unpublished",
      });
      setVersions([]);
      versionsRef.current = [];
      versionsLoadedRef.current = true;
      setActiveVersionId("");
      setDraft(initialDraft);
      baselineDraft.current = initialDraft;
      return;
    }
    if (!agentId) throw new Error("Agent id is missing.");
    if (loadedAgentId.current !== agentId) {
      loadedAgentId.current = agentId;
      versionsRef.current = [];
      versionsLoadedRef.current = false;
      versionsRequestRef.current = null;
      setVersions([]);
    }
    const [agentDoc, currentVersion] = await Promise.all([
      core<AgentDocument>(`/api/v1/agents/${encodeURIComponent(agentId)}?view=summary`),
      core<AgentVersion>(`/api/v1/agents/${encodeURIComponent(agentId)}/versions/current`),
    ]);
    setAgent(agentDoc);
    setActiveVersionId(currentVersion.version_id);
    const nextDraft = normalizeVersion(currentVersion, agentDoc);
    setDraft(nextDraft);
    baselineDraft.current = nextDraft;
  }, [
    agentId,
    autoBuilderSessionId,
    scoutSessionId,
    isAutoBuilderDraft,
    isNew,
    requestedArchitecture,
  ]);

  // Which agent the cached toolOptions were fetched for. The catalog is
  // agent-scoped (a tool document may carry an `agent_ids` allowlist so
  // client-specific packs don't clutter every agent's picker), so switching
  // agents has to refetch rather than reuse the previous agent's list.
  const toolsLoadedForRef = useRef<string | null>(null);

  const ensureTools = useCallback(async () => {
    if (toolsLoaded && toolsLoadedForRef.current === agentId) return toolOptions;
    const query = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : "";
    const rows = await core<ToolOption[]>(
      `/api/v1/agents/workflow-function-tools${query}`,
    );
    const normalized = Array.isArray(rows) ? rows : [];
    setToolOptions(normalized);
    setToolsLoaded(true);
    toolsLoadedForRef.current = agentId;
    return normalized;
  }, [agentId, toolOptions, toolsLoaded]);

  const ensureVersions = useCallback(async () => {
    if (isNew) return [] as AgentVersion[];
    if (versionsLoadedRef.current) return versionsRef.current;
    if (versionsRequestRef.current) return versionsRequestRef.current;
    setVersionsLoading(true);
    const request = core<AgentVersion[]>(
        `/api/v1/agents/${encodeURIComponent(agentId)}/versions/summary?limit=200`,
      ).then((rows) => {
      const normalized = Array.isArray(rows) ? rows : [];
      setVersions(normalized);
      versionsRef.current = normalized;
      versionsLoadedRef.current = true;
      return normalized;
    }).finally(() => {
      versionsRequestRef.current = null;
      setVersionsLoading(false);
    });
    versionsRequestRef.current = request;
    return request;
  }, [agentId, isNew]);

  useEffect(() => {
    let active = true;
    load()
      .catch((error) => active && toast.danger("Could not load agent", (error as Error).message))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [load, toast]);

  useEffect(() => {
    if (section === "tools") {
      void ensureTools().catch((error) => toast.danger("Could not load tools", (error as Error).message));
    }
    if (section === "versions") {
      void ensureVersions().catch((error) => toast.danger("Could not load versions", (error as Error).message));
    }
  }, [ensureTools, ensureVersions, section, toast]);

  const reloadExternalPrompt = useCallback(() => {
    // An explicit reload starts a fresh poll budget (a manual Check-now means
    // someone is watching again). Poll ticks bump externalPromptReloads
    // directly and never reset this.
    externalPromptPolls.current = 0;
    setExternalPromptPollGaveUp(false);
    setExternalPromptReloads((count) => count + 1);
    versionsLoadedRef.current = false;
    void load().then(() => ensureVersions()).catch((error) => toast.danger("Could not reload agent", (error as Error).message));
  }, [load, ensureVersions, toast]);
  const externalSessionId = isNew ? scoutAppliedSession || autoBuilderSessionId || scoutSessionId : "";

  useEffect(() => {
    setExternalPromptError("");
    // Fetch for clients in external mode AND for admins viewing any existing agent
    // so the split-view panel is visible when the agent owner has it enabled.
    if (!(externalMode || externalAdmin.can) || (!agentId && !externalSessionId)) return;
    let active = true;
    setExternalPromptLoading(true);
    const endpoint = externalSessionId
      ? `/api/v1/prompt-builder/sessions/${encodeURIComponent(externalSessionId)}/external-prompt`
      : `/api/v1/agents/${encodeURIComponent(agentId)}/external-prompt`;
    core<ExternalPromptResponse>(endpoint)
      .then((result) => active && setExternalPrompt(result))
      .catch((error) => {
         if (active) setExternalPromptError((error as Error).message || "Could not prepare the business sections. Please retry.");
      })
      .finally(() => active && setExternalPromptLoading(false));
    return () => { active = false; };
  }, [agentId, externalSessionId, externalMode, externalAdmin.can, externalPromptReloads]);

  useEffect(() => {
    setExternalPrompt(null);
    setExternalPromptDirty(false);
    externalPromptPolls.current = 0;
    setExternalPromptPollGaveUp(false);
  }, [agentId, externalSessionId]);

  useEffect(() => {
    if (externalPrompt?.status !== "pending" || externalPromptLoading) return undefined;
    if (externalPromptPolls.current >= 30) {
      setExternalPromptPollGaveUp(true);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      externalPromptPolls.current += 1;
      setExternalPromptReloads((count) => count + 1);
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [externalPrompt?.status, externalPromptLoading, externalPromptReloads]);

  useEffect(() => {
    if (externalMode && isNew && !externalSessionId && !scoutConversationId) {
      navigate("/home/autobuilder/single/new", { replace: true });
    }
  }, [externalMode, isNew, externalSessionId, scoutConversationId, navigate]);

  useEffect(() => {
    if (isNew || !agentId) {
      setHasPendingSchedule(false);
      return;
    }
    core<{ has_pending?: boolean }>(
      `/api/v1/agents/${encodeURIComponent(agentId)}/has-pending-schedule${activeVersionId ? `?version=${encodeURIComponent(activeVersionId)}` : ""}`,
    )
      .then((result) => setHasPendingSchedule(result.has_pending === true))
      .catch(() => setHasPendingSchedule(false));
  }, [activeVersionId, agentId, isNew]);

  const updateDraft = <K extends keyof AgentDraft>(key: K, value: AgentDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  const saveExternalMetadata = async () => {
    if (!agentId || savingMetadata || !metadataDirty) return;
    const agentName = draft.agentName.trim();
    if (agentName.length < 2) {
      toast.warning("Agent name required", "Use at least two characters.");
      return;
    }
    try {
      setSavingMetadata(true);
      const updated = await core<AgentDocument>(`/api/v1/agents/${encodeURIComponent(agentId)}`, {
        method: "PATCH",
        body: JSON.stringify({
          agent_name: agentName,
          description: draft.description.trim(),
        }),
      });
      setAgent(updated);
      setDraft((current) => ({ ...current, agentName, description: draft.description.trim() }));
      baselineDraft.current = {
        ...baselineDraft.current,
        agentName,
        description: draft.description.trim(),
      };
      toast.success("Agent settings saved", agentName);
    } catch (error) {
      toast.danger("Could not save agent settings", (error as Error).message);
    } finally {
      setSavingMetadata(false);
    }
  };

  const discardLocalChanges = useCallback(() => {
    setDraft({ ...baselineDraft.current });
  }, []);
  const requestDiscard = useDiscardWarning({ dirty, discard: discardLocalChanges });

  const changeSection = (nextSection: StudioSection) => {
    if (nextSection === section) return;
    if (nextSection !== "prompt") setNormalPromptExpanded(false);
    setSection(nextSection);
  };

  const goBack = () => {
    void requestDiscard(() => navigate("/"));
  };

  const promptPayload = {
    system_prompt: draft.systemPrompt,
    first_message: draft.firstMessage,
    call_infields: draft.infields.map((field) => ({
      fieldName: field.fieldName.trim(),
      fieldType: field.fieldType || "string",
      visible: field.visible !== false,
      normalise: field.normalise === true,
      emphasis: field.emphasis === true,
      to_lowercase: field.to_lowercase === true,
      number_to_words: field.number_to_words === true,
      infield_language: field.infield_language || "NA",
      description: field.description || "",
    })).filter((field) => field.fieldName),
    call_outcomes: draft.outcomes.map((field) => ({
      fieldName: field.fieldName.trim(),
      fieldType: field.fieldType || "string",
      visible: field.visible !== false,
      description: field.description || "",
      validValues: field.validValues || [],
      defaultValue: (field.defaultValue || "").trim(),
      retryValues: field.retryValues || [],
      successValues: field.successValues || [],
      ...(field.sections ? { sections: field.sections } : {}),
      ...(field.freeText ? { freeText: true } : {}),
      ...(field.acceptedFindings?.length ? { acceptedFindings: field.acceptedFindings } : {}),
      exclusive_group: (field.exclusive_group || "").trim(),
      outcome_priority: (field.exclusive_group || "").trim() ? field.outcome_priority ?? null : null,
      is_primary: field.fieldName.trim() === (draft.primaryOutcome || ""),
    })).filter((field) => field.fieldName),
    outcome_single_winner: draft.outcomeSingleWinner,
    primary_outcome: draft.primaryOutcome || null,
    tool: draft.tools,
    sentence_catalogue: draft.sentenceCatalogue
      .map((item) => ({ name: item.name.trim(), sentence: item.sentence }))
      .filter((item) => item.name),
    thinker_cases: draft.thinkerCases.split("\n").map((value) => value.trim()).filter(Boolean),
    talker_cases: draft.talkerCases.split("\n").map((value) => value.trim()).filter(Boolean),
    stt_language: draft.sttLanguage,
    language_switch: draft.languageSwitch,
    primary_language: draft.languagePreference.primaryLanguage,
    secondary_language: draft.languagePreference.secondaryLanguage,
    language_switch_percent: draft.languagePreference.primaryLanguage
      ? draft.languagePreference.languageSwitchPercent ?? DEFAULT_SWITCH_PERCENT
      : null,
    calendar: promptUsesCalendarVariables(draft.systemPrompt, draft.firstMessage),
    transfer: draft.transfer,
  };

  // What the outcome check compares: this draft's outcomes against the stored version.
  const outcomeCheckRequest = {
    ...(isNew ? {} : { agent_id: agentId }),
    call_outcomes: promptPayload.call_outcomes,
    tools: promptPayload.tool,
  };
  // Live check while the outcomes tab is open (debounced). Quiet when core's mode is "off".
  useEffect(() => {
    if (section !== "call-data") return undefined;
    const timer = window.setTimeout(() => {
      void outcomeGate.check(outcomeCheckRequest, { advisoryOnly: isNew });
    }, 900);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, draft.outcomes, agentId, isNew]);
  const architecture = agent?.agent_type === "thinker_talker" || requestedArchitecture === "thinker_talker"
    ? "thinker_talker"
    : "single";
  const isThinkerTalker = architecture === "thinker_talker";

  useEffect(() => {
    if (showExternalWorkspace && section !== "prompt") setSection("prompt");
  }, [showExternalWorkspace, section]);
  const sectionItems = showExternalWorkspace
    ? externalPromptSections
    : isThinkerTalker
      ? thinkerTalkerSections
      : singleAgentSections;

  const mentionOptions = useMemo(
    () => getPromptMentionOptions(draft.infields, draft.sentenceCatalogue),
    [draft.infields, draft.sentenceCatalogue],
  );
  const systemPromptIssues = useMemo(
    () => findMalformedPromptVariables(draft.systemPrompt),
    [draft.systemPrompt],
  );
  const firstMessageIssues = useMemo(
    () => findMalformedPromptVariables(draft.firstMessage),
    [draft.firstMessage],
  );

  const handlePromptTextChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    field: "systemPrompt" | "firstMessage",
  ) => {
    const value = event.target.value;
    const cursorIndex = event.target.selectionStart || 0;
    updateDraft(field, value);
    setMentionMenu(
      value.slice(0, cursorIndex).endsWith("@")
        ? { field, cursorIndex, selectedIndex: 0 }
        : { field: null, cursorIndex: 0, selectedIndex: 0 },
    );
  };

  const insertMention = (option: { insertText?: string; kind?: string; label?: string }) => {
    if (!mentionMenu.field) return;
    const current = draft[mentionMenu.field];
    const before = current.slice(0, mentionMenu.cursorIndex - 1);
    const after = current.slice(mentionMenu.cursorIndex);
    const inserted = option.insertText || `{{${option.label || ""}}}`;
    updateDraft(mentionMenu.field, `${before}${inserted}${after}`);
    const targetField = mentionMenu.field;
    setMentionMenu({ field: null, cursorIndex: 0, selectedIndex: 0 });
    requestAnimationFrame(() => {
      const target = targetField === "systemPrompt" ? systemPromptRef.current : firstMessageRef.current;
      const cursor = before.length + inserted.length;
      target?.focus();
      target?.setSelectionRange(cursor, cursor);
    });
  };

  const handlePublishClick = () => {
    if (!draft.systemPrompt.trim()) {
      toast.warning("System prompt required", "Add a system prompt before publishing.");
      setSection("prompt");
      return;
    }
    const languageProblem = draft.languageSwitch
      ? languagePreferenceProblem(draft.languagePreference, draft.sttLanguage)
      : null;
    if (languageProblem) {
      toast.warning("Check reply languages", languageProblem);
      setSection("runtime");
      return;
    }
    const promptTexts = [
      draft.systemPrompt,
      draft.firstMessage,
      ...draft.sentenceCatalogue.map((item) => item.sentence),
    ];
    const malformedVariables = promptTexts.flatMap((text) => findMalformedPromptVariables(text));
    if (malformedVariables.length || systemPromptIssues.length || firstMessageIssues.length) {
      toast.warning("Prompt syntax error", "Use the {{variable}} format for prompt variables.");
      setSection("prompt");
      return;
    }
    const declaredInfields = new Set(draft.infields.map((field) => field.fieldName.trim().toLowerCase()));
    const undeclaredVariables = [...new Set(promptTexts.flatMap((text) => extractPromptVariables(text)))]
      .filter((name) => !isBuiltInPromptVariable(name) && !declaredInfields.has(name.toLowerCase()));
    if (undeclaredVariables.length) {
      toast.warning(
        "Prompt variables are not declared",
        undeclaredVariables.join(", "),
      );
    }
    const nearbyPlacesTool = draft.tools.find((tool) => tool.actual_name === "search_nearby_places");
    if (nearbyPlacesTool && !nearbyPlacesTool.keyword?.trim()) {
      toast.warning("Tool keyword required", "Add a nearby places search keyword before publishing.");
      setSection("tools");
      return;
    }
    const lookupDateTool = draft.tools.find((tool) => tool.actual_name === "lookup_date");
    if (lookupDateTool && !String(lookupDateTool.keyword || "").trim()) {
      toast.warning("Scheduling window required", "Set the lookup_date scheduling window before publishing.");
      setSection("tools");
      return;
    }
    if (isThinkerTalker && (!draft.thinkerCases.trim() || !draft.talkerCases.trim())) {
      toast.warning(
        "Reasoning routes required",
        "Add at least one Thinker route and one Talker route before publishing.",
      );
      setSection("reasoning");
      return;
    }

    const nextVersionNumber = Math.max(
      1,
      Number(agent?.version_count || versions.length || 0) + 1,
    );
    setNewVersionName(
      isNew
        ? draft.versionName.trim() || "Version 1"
        : `Version ${nextVersionNumber}`,
    );
    setOverwriteVersionName(draft.versionName.trim() || "Current version");
    setPublishMode("new_version");
    setPublishDialogOpen(true);
  };

  const publish = async (mode: PublishMode, versionName: string, overrides: OutcomeOverride[] = []) => {
    const resolvedVersionName = versionName.trim();
    if (!resolvedVersionName) {
      toast.warning("Version name required", "Enter a name before publishing.");
      return;
    }
    if (mode === "overwrite" && (isNew || !activeVersionId || hasPendingSchedule)) {
      toast.warning(
        "Version cannot be overwritten",
        hasPendingSchedule
          ? "A scheduled call is using this version. Create a new version instead."
          : "Create the first version before using overwrite.",
      );
      return;
    }

    try {
      setPublishing(true);
      // Outcome check: advice for everyone; in enforce mode it can stop the publish until the author
      // fixes the problem or accepts it with a reason. A new agent is only advised (core never blocks creates).
      if (!overrides.length) {
        const stopped = await outcomeGate.stopsPublish(
          {
            ...(isNew ? {} : { agent_id: agentId }),
            ...(!isNew && mode === "overwrite" && activeVersionId ? { version_id: activeVersionId } : {}),
            call_outcomes: promptPayload.call_outcomes,
            tools: promptPayload.tool,
          },
          (accepted) => publish(mode, versionName, accepted),
          { advisoryOnly: isNew },
        );
        if (stopped) {
          setPublishDialogOpen(false);
          return;
        }
      }
      if (isNew) {
        const created = await core<AgentDocument>("/api/v1/agents", {
          method: "POST",
          body: JSON.stringify({
            agent_name: draft.agentName.trim(),
            description: draft.description.trim(),
            status: "Active",
            agent_type: architecture,
            version_name: resolvedVersionName,
            prompt: promptPayload,
          }),
        });
        if (isAutoBuilderDraft || scoutAppliedSession) {
          try {
            await core(
              `/api/v1/prompt-builder/sessions/${encodeURIComponent(scoutAppliedSession || autoBuilderSessionId)}/complete`,
              {
                method: "POST",
                body: JSON.stringify({
                  agent_id: created.agent_id,
                  agent_version_id: created.current_version,
                }),
              },
            );
          } catch (error) {
            toast.warning(
              "Agent published",
              `AutoBuilder session linkage could not be completed: ${(error as Error).message}`,
            );
          }
        }
        setAgent(created);
        baselineDraft.current = { ...draft, versionName: resolvedVersionName };
        setPublishDialogOpen(false);
        toast.success("Agent published", draft.agentName);
        navigate(`/home/create-agent?agent_id=${encodeURIComponent(created.agent_id)}`, { replace: true });
        return;
      }

      let next: AgentDocument;
      if (mode === "overwrite") {
        await core(
          `/api/v1/agents/${encodeURIComponent(agentId)}/versions/${encodeURIComponent(activeVersionId)}`,
          {
            method: "PUT",
            body: JSON.stringify({
              version_name: resolvedVersionName,
              prompt: promptPayload,
              ...(overrides.length ? { outcome_overrides: overrides } : {}),
            }),
          },
        );
        next = await core<AgentDocument>(`/api/v1/agents/${encodeURIComponent(agentId)}`, {
          method: "PATCH",
          body: JSON.stringify({
            agent_name: draft.agentName.trim(),
            description: draft.description.trim(),
            status: "Active",
            agent_type: architecture,
          }),
        });
      } else {
        next = await core<AgentDocument>(`/api/v1/agents/${encodeURIComponent(agentId)}`, {
          method: "PATCH",
          body: JSON.stringify({
            agent_name: draft.agentName.trim(),
            description: draft.description.trim(),
            status: "Active",
            agent_type: architecture,
            version_name: resolvedVersionName,
            prompt: promptPayload,
            ...(overrides.length ? { outcome_overrides: overrides } : {}),
          }),
        });
      }
      await core(`/api/v1/agents/${encodeURIComponent(agentId)}/publish`, { method: "POST" });
      if (scoutAppliedSession) {
        try {
          await core(`/api/v1/prompt-builder/sessions/${encodeURIComponent(scoutAppliedSession)}/complete`, {
            method: "POST",
            body: JSON.stringify({ agent_id: agentId, agent_version_id: mode === "overwrite" ? activeVersionId : next.current_version }),
          });
        } catch (error) {
          toast.warning("Agent published", `Scout history linkage could not be completed: ${(error as Error).message}`);
        }
      }
      setAgent(next);
      baselineDraft.current = { ...draft, versionName: resolvedVersionName };
      setPublishDialogOpen(false);
      await load();
      void outcomeGate.check(outcomeCheckRequest);
      const newVersions = await refreshVersions();
      if (mode === "new_version" && newVersions && newVersions.length > 0) {
        setActiveVersionId(newVersions[0].version_id);
      }
      toast.success(
        mode === "overwrite" ? "Version overwritten" : "New version published",
        resolvedVersionName,
      );
    } catch (error) {
      const stoppedFindings = findingsFromError(error);
      if (stoppedFindings) {
        setPublishDialogOpen(false);
        outcomeGate.openBlock(stoppedFindings, (accepted) => publish(mode, versionName, accepted));
      } else {
        toast.danger("Publish failed", (error as Error).message);
      }
    } finally {
      setPublishing(false);
    }
  };

  const refreshVersions = async () => {
    if (isNew) return;
    try {
      setVersionsLoading(true);
      const rows = await core<AgentVersion[]>(
        `/api/v1/agents/${encodeURIComponent(agentId)}/versions/summary?limit=200`,
      );
      const normalized = Array.isArray(rows) ? rows : [];
      setVersions(normalized);
      versionsRef.current = normalized;
      versionsLoadedRef.current = true;
      return normalized;
    } catch (error) {
      toast.danger("Could not refresh versions", (error as Error).message);
    } finally {
      setVersionsLoading(false);
    }
  };

  const editVersion = (version: AgentVersion) => {
    void requestDiscard(() => {
      if (!agent) return;
      const nextDraft = normalizeVersion(version, agent);
      setActiveVersionId(version.version_id);
      setDraft(nextDraft);
      baselineDraft.current = nextDraft;
      setSection("prompt");
      toast.success("Version loaded", version.version_name || version.version_id);
    });
  };

  const createVersion = async () => {
    if (isNew) {
      toast.warning("Publish before versioning", "Version history starts after the agent is published.");
      return;
    }
    const name = window.prompt("New version name", `Version ${versions.length + 1}`)?.trim();
    if (!name) return;
    setDraft((current) => ({
      ...EMPTY_DRAFT,
      agentName: current.agentName,
      description: current.description,
      versionName: name,
      sttLanguage: current.sttLanguage,
      languageSwitch: current.languageSwitch,
      languagePreference: current.languagePreference,
    }));
    setActiveVersionId("");
    setSection("prompt");
    toast.success("Local version started", "Nothing is saved until Publish.");
  };

  const deleteVersion = async (version: AgentVersion) => {
    if (!window.confirm(`Delete "${version.version_name || version.version_id}"?`)) return;
    try {
      await core(
        `/api/v1/agents/${encodeURIComponent(agentId)}/versions/${encodeURIComponent(version.version_id)}`,
        { method: "DELETE" },
      );
      await refreshVersions();
      toast.success("Version deleted", version.version_name || version.version_id);
    } catch (error) {
      toast.danger("Could not delete version", (error as Error).message);
    }
  };

  const scoutContext = useMemo(() => ({ agent_name: draft.agentName, primary_language: draft.sttLanguage, system_prompt: draft.systemPrompt, first_message: draft.firstMessage, tools: draft.tools, infields: draft.infields, outcomes: draft.outcomes, editor_snapshot: JSON.stringify(draft) }), [draft]);
  const handleScoutConversation = useCallback((conversation: ScoutConversation) => {
    if (conversation.conversation_id === scoutConversationId && (conversation.session_id || "") === scoutSessionId) return;
    const params = new URLSearchParams(searchParams);
    params.set("scout_conversation_id", conversation.conversation_id);
    if (conversation.session_id) params.set("scout_session_id", conversation.session_id);
    else params.delete("scout_session_id");
    navigate(`/home/create-agent?${params.toString()}`, { replace: true });
  }, [navigate, scoutConversationId, scoutSessionId, searchParams]);
  const handleApplyScoutDraft = useCallback(async (sessionId: string, sourceContext: Record<string, unknown>) => {
    if (externalMode) {
      navigate(`/home/create-agent?autobuilder_session_id=${encodeURIComponent(sessionId)}`, { replace: true });
      return;
    }
    const original = draftRef.current;
    if (sourceContext.editor_snapshot && sourceContext.editor_snapshot !== JSON.stringify(original)) {
      throw new Error("Your draft changed after this conversation began. Start a new conversation with the current draft to avoid overwriting your edits.");
    }
    if (!sourceContext.editor_snapshot && original.systemPrompt.trim()) {
      throw new Error("This draft now contains a prompt. Start Scout from this draft before replacing it.");
    }
    const generated = await core<AutoBuilderStudioDraft>(`/api/v1/prompt-builder/sessions/${encodeURIComponent(sessionId)}/studio-draft`);
    if (draftRef.current !== original) throw new Error("Your draft changed while loading the result. Review it and try again.");
    const prompt = generated.prompt;
    setBeforeScout(original);
    setDraft({ ...original, agentName: generated.agent_name || original.agentName, systemPrompt: prompt.system_prompt || "", firstMessage: prompt.first_message || "", tools: prompt.tool || [], infields: prompt.call_infields || [], outcomes: prompt.call_outcomes || [], sttLanguage: prompt.stt_language || original.sttLanguage, languageSwitch: prompt.language_switch ?? original.languageSwitch, languagePreference: preferenceAfterSttChange(original.languagePreference, prompt.stt_language || original.sttLanguage, prompt.language_switch ?? original.languageSwitch), transfer: prompt.transfer ?? original.transfer });
    setScoutAppliedSession(sessionId);
    toast.success("Applied to draft", "Review or test your agent, then publish when ready.");
  }, [toast, externalMode, navigate]);
  const undoScout = () => { if (beforeScout) { setDraft(beforeScout); setBeforeScout(null); setScoutAppliedSession(""); } };

  const selectedToolKeys = useMemo(
    () => new Set(draft.tools.map((tool) => tool.function_tool_id || tool.actual_name || tool.name)),
    [draft.tools],
  );
  const routingReady = !isThinkerTalker || Boolean(draft.thinkerCases.trim() && draft.talkerCases.trim());
  const promptReady = Boolean(
    draft.agentName.trim()
    && draft.systemPrompt.trim()
    && draft.firstMessage.trim()
    && routingReady
  );

  if (loading && !agent) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center bg-canvas">
        <Loader2 className="h-5 w-5 animate-spin text-brand" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center bg-canvas text-sm text-danger">
        Agent could not be loaded.
      </div>
    );
  }

  return (
    <div className="agent-studio flex h-full min-h-0 flex-col overflow-hidden bg-canvas">
      <header className="flex h-16 shrink-0 items-center justify-between border-b border-line bg-elevated px-5">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={goBack}
            aria-label="Back to agents"
            className="rounded-md p-2 text-ink-3 hover:bg-soft hover:text-ink"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="h-6 w-px bg-line" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {showExternalWorkspace ? (
                <h1 className="min-w-0 max-w-72 truncate text-sm font-bold text-ink">{draft.agentName}</h1>
              ) : (
                <input
                  value={draft.agentName}
                  onChange={(event) => updateDraft("agentName", event.target.value)}
                  maxLength={50}
                  className="min-w-0 max-w-72 border-0 bg-transparent text-sm font-bold text-ink outline-none"
                  aria-label="Agent name"
                />
              )}
              <span className={cn(
                "rounded px-2 py-0.5 text-[10px] font-bold uppercase",
                agent.status === "Active" ? "bg-success-soft text-success" : "bg-warning-soft text-warning",
              )}>
                {isNew ? "Unpublished" : agent.status || "Draft"}
              </span>
              {isThinkerTalker && (
                <span className="inline-flex items-center gap-1 rounded bg-brand-soft px-2 py-0.5 text-[10px] font-bold uppercase text-brand">
                  <BrainCircuit className="h-3 w-3" /> Thinker-Talker
                </span>
              )}
              {(showExternalWorkspace ? metadataDirty || externalPromptDirty : dirty) && (
                <span className="text-[10px] font-semibold text-warning">Unsaved</span>
              )}
            </div>
            <div className="flex min-w-0 items-center gap-1 truncate font-mono text-[10px] text-ink-3">
              <span className="shrink-0">{draft.versionName} /</span>
              {isNew ? (
                <span>Not saved</span>
              ) : (
                <>
                  <span className="inline-flex min-w-0 items-center gap-0.5">
                    <span className="truncate">{activeVersionId || "Version ID pending"}</span>
                    {activeVersionId && <CopyButton value={activeVersionId} className="shrink-0" />}
                  </span>
                  <span className="shrink-0">/</span>
                  <span className="inline-flex min-w-0 items-center gap-0.5">
                    <span className="truncate">{agent.agent_id}</span>
                    {agent.agent_id && <CopyButton value={agent.agent_id} className="shrink-0" />}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(showExternalWorkspace ? metadataDirty || externalPromptDirty : dirty) && (
            <span className="rounded-md bg-warning-soft px-3 py-2 text-[10px] font-bold uppercase text-warning">
              {showExternalWorkspace ? "Unsaved changes" : "Local until publish"}
            </span>
          )}
          {(!showExternalWorkspace || isNew) && <ScoutButton
            onClick={() => setScoutOpen((p) => !p)}
            aria-pressed={scoutOpen}
          />}
          {/* In external mode this would publish the internal draft, which
              the API refuses for anyone but an admin. The template has its
              own Publish, and two of them on one screen mean two different
              things. */}
          {!showExternalWorkspace && (
            <button
              type="button"
              onClick={handlePublishClick}
              disabled={publishing || !promptReady}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-brand px-3 text-xs font-bold text-white hover:bg-brand-hover shadow-xs transition-colors disabled:cursor-not-allowed disabled:bg-line disabled:text-ink-3"
            >
              {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Publish
            </button>
          )}
        </div>
      </header>

      {showExternalWorkspace ? (
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          <div className={cn(
            "mx-auto grid min-h-full w-full max-w-[1800px] gap-3",
            !externalPromptExpanded && "xl:grid-cols-[minmax(0,1fr)_minmax(380px,0.48fr)]",
          )}>
            <main className="min-w-0 space-y-3">
              <section className="rounded-xl border border-line bg-elevated shadow-sm">
                <div className="flex flex-col gap-3 border-b border-line p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-soft text-ink-2">
                      <Settings2 className="h-4 w-4" />
                    </span>
                    <div>
                      <h2 className="text-sm font-bold text-ink">Agent settings</h2>
                      <p className="mt-0.5 text-[11px] text-ink-3">Basic details shown across your workspace.</p>
                    </div>
                  </div>
                  {!isNew && (
                    <button
                      type="button"
                      onClick={() => void saveExternalMetadata()}
                      disabled={!metadataDirty || savingMetadata}
                      className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-line bg-elevated px-3 text-xs font-bold text-ink hover:border-brand disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {savingMetadata ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      {savingMetadata ? "Saving…" : "Save settings"}
                    </button>
                  )}
                </div>
                <div className="grid gap-4 p-5 md:grid-cols-[minmax(220px,0.8fr)_minmax(280px,1.2fr)]">
                  <label className="block">
                    <span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Agent name</span>
                    <input
                      value={draft.agentName}
                      onChange={(event) => updateDraft("agentName", event.target.value)}
                      maxLength={50}
                      disabled={isNew}
                      className="h-10 w-full rounded-md border border-line bg-soft px-3 text-sm font-semibold text-ink outline-none focus:border-brand disabled:cursor-not-allowed disabled:opacity-70"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Description</span>
                    <input
                      value={draft.description}
                      onChange={(event) => updateDraft("description", event.target.value)}
                      maxLength={150}
                      disabled={isNew}
                      placeholder="What this agent owns"
                      className="h-10 w-full rounded-md border border-line bg-soft px-3 text-sm text-ink outline-none focus:border-brand disabled:cursor-not-allowed disabled:opacity-70"
                    />
                  </label>
                </div>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-line bg-soft/50 px-5 py-3 font-mono text-[10px] text-ink-3">
                  <span>Status <strong className="ml-1 text-ink">{isNew ? "Ready to create" : agent.status || "Draft"}</strong></span>
                  <span>Version <strong className="ml-1 text-ink">{draft.versionName || activeVersionId || "Pending"}</strong></span>
                  {isNew && <span className="font-sans text-ink-3">Name and description come from your guided setup.</span>}
                </div>
              </section>

              <ExternalPromptEditor
                agentId={agentId}
                sessionId={externalSessionId || undefined}
                error={externalPromptError}
                data={externalPrompt}
                loading={externalPromptLoading}
                reload={reloadExternalPrompt}
                canAdminister={externalAdmin.can}
                onDirtyChange={setExternalPromptDirty}
                pollGaveUp={externalPromptPollGaveUp}
                expanded={externalPromptExpanded}
                onExpandedChange={setExternalPromptExpanded}
              />
            </main>

            <aside className={cn("min-w-0 space-y-3 xl:sticky xl:top-0 xl:self-start", externalPromptExpanded && "hidden")}>
              <div className="rounded-xl border border-line bg-elevated p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-bold text-ink">Test your agent</h2>
                    <p className="mt-1 text-[11px] leading-5 text-ink-3">Calls always use a published version so the result matches production.</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-success-soft px-2 py-1 font-mono text-[9px] font-bold uppercase text-success">Published only</span>
                </div>
              </div>
              <AgentTestWorkspace
                allowTranscriptExpand
                agentId={agent.agent_id}
                agentName={draft.agentName}
                architecture={architecture}
                activeVersionId={activeVersionId}
                versions={versions}
                isNew={isNew}
                dirty={externalPromptDirty || metadataDirty}
                dirtyReason="Save agent settings and publish prompt changes before testing."
                ensureVersions={ensureVersions}
                loadVersionDetails={(versionId) => core<AgentVersion>(
                  `/api/v1/agents/${encodeURIComponent(agentId)}/versions/${encodeURIComponent(versionId)}`,
                )}
              />
            </aside>
          </div>
        </div>
      ) : (
      <PanelGroup direction="horizontal" className="min-h-0 flex-1 overflow-hidden p-3">
        <Panel defaultSize={50} minSize={30} className={cn("flex flex-col min-h-0", compactStudio && scoutOpen && "hidden")}>
          <main className="min-h-0 h-full overflow-y-auto bg-transparent">
          {/* External mode replaces the internal prompt editor entirely. The
              admin switch stays reachable so whoever turned it on can turn it
              back off; a raw system_prompt write is refused by the API for
              everyone else regardless of what this renders. */}
          {section === "prompt" && (externalMode || (externalAdmin.can && externalPrompt?.enabled)) && (
            <div className="min-h-full space-y-3">
              <ExternalPromptEditor
                agentId={agentId}
                sessionId={externalSessionId || undefined}
                error={externalPromptError}
                data={externalPrompt}
                loading={externalPromptLoading}
                reload={reloadExternalPrompt}
                canAdminister={externalAdmin.can}
                pollGaveUp={externalPromptPollGaveUp}
              />
            </div>
          )}

          {section === "prompt" && !externalMode && !(externalAdmin.can && externalPrompt?.enabled) && (
            <div className="grid min-h-full grid-rows-[104px_minmax(360px,1fr)] gap-3">
              <section className="relative order-2 flex min-h-0 flex-col overflow-visible rounded-md border border-line bg-elevated shadow-sm">
                <div className="sticky top-0 z-20 flex min-h-12 shrink-0 flex-wrap items-center justify-between gap-2 border-b border-line bg-elevated px-5 py-2 shadow-sm">
                  <div>
                    <h2 className="text-sm font-bold text-ink">System prompt</h2>
                    <p className="text-[10px] text-ink-3">The operating contract used on every call.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] text-ink-3">{draft.systemPrompt.length.toLocaleString()} chars</span>
                    <button
                      type="button"
                      onClick={() => setEditingSystemPrompt((current) => !current)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line bg-soft px-2.5 text-[11px] font-bold text-ink hover:bg-elevated"
                    >
                      {editingSystemPrompt ? <Sparkles className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                      {editingSystemPrompt ? "Preview" : "Edit markdown"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setNormalPromptExpanded((current) => !current)}
                      aria-pressed={normalPromptExpanded}
                      title={normalPromptExpanded ? "Show test panel" : "Hide test panel and widen prompt"}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line bg-soft px-2.5 text-[11px] font-bold text-ink hover:bg-elevated"
                    >
                      {normalPromptExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                      {normalPromptExpanded ? "Show test panel" : "Focus prompt"}
                    </button>
                  </div>
                </div>
                {editingSystemPrompt ? (
                  <textarea
                    ref={systemPromptRef}
                    aria-label="System prompt markdown editor"
                    value={draft.systemPrompt}
                    onChange={(event) => handlePromptTextChange(event, "systemPrompt")}
                    placeholder="Define the agent's role, conversation flow, factual boundaries, escalation rules, and tool usage..."
                    className={cn(
                      "min-h-0 flex-1 resize-none border-0 bg-elevated p-5 font-mono text-[13px] leading-6 text-ink outline-none",
                      systemPromptIssues.length > 0 && "ring-1 ring-inset ring-danger",
                    )}
                  />
                ) : (
                  <PromptMarkdown
                    content={draft.systemPrompt}
                    ariaLabel="System prompt markdown preview"
                    emptyMessage="No system prompt has been entered. Choose Edit markdown to add one."
                    className="min-h-0 flex-1 rounded-none border-0 bg-elevated px-5 py-4"
                  />
                )}
                <div className="flex h-8 shrink-0 items-center justify-between border-t border-line bg-soft px-5 text-[10px] text-ink-3">
                  <span>{editingSystemPrompt ? "Type @ to insert an input, sentence, or calendar token." : "Formatted Markdown preview"}</span>
                  {systemPromptIssues.length > 0 && <span className="font-bold text-danger">Malformed variable syntax</span>}
                </div>
                {editingSystemPrompt && mentionMenu.field === "systemPrompt" && (
                  <PromptTokenMenu
                    options={mentionOptions}
                    selectedIndex={mentionMenu.selectedIndex}
                    onSelect={insertMention}
                    onHover={(selectedIndex: number) => setMentionMenu((current) => ({ ...current, selectedIndex }))}
                    style={{ left: "20px", bottom: "36px" }}
                  />
                )}
              </section>
              <section className="relative order-1 flex min-h-0 flex-col overflow-visible rounded-md border border-line bg-elevated shadow-sm">
                <div className="flex h-11 shrink-0 items-center gap-2 border-b border-line px-5">
                  <MessageSquareText className="h-4 w-4 text-ink-3" />
                  <h2 className="text-xs font-bold text-ink">First message</h2>
                </div>
                <input
                  ref={firstMessageRef}
                  type="text"
                  value={draft.firstMessage}
                  onChange={(event) => handlePromptTextChange(event, "firstMessage")}
                  placeholder="The exact first sentence spoken when the call connects."
                  className={cn(
                    "min-h-0 flex-1 border-0 bg-elevated px-5 text-sm text-ink outline-none",
                    firstMessageIssues.length > 0 && "ring-1 ring-inset ring-danger",
                  )}
                />
                {mentionMenu.field === "firstMessage" && (
                  <PromptTokenMenu
                    options={mentionOptions}
                    selectedIndex={mentionMenu.selectedIndex}
                    onSelect={insertMention}
                    onHover={(selectedIndex: number) => setMentionMenu((current) => ({ ...current, selectedIndex }))}
                    style={{ left: "20px", bottom: "52px" }}
                  />
                )}
              </section>
            </div>
          )}

          {section === "content" && (
            <div className="min-h-full rounded-md border border-line bg-elevated p-4 shadow-sm">
              <SentenceLibrary
                items={draft.sentenceCatalogue}
                fields={draft.infields}
                onChange={(items) => updateDraft("sentenceCatalogue", items)}
              />
            </div>
          )}

          {section === "tools" && (
            <div className="min-h-full overflow-hidden rounded-md border border-line bg-elevated shadow-sm">
              <div className="flex h-14 items-center justify-between border-b border-line px-5">
                <div>
                  <h2 className="text-sm font-bold text-ink">Function tools</h2>
                  <p className="text-[10px] text-ink-3">Only backend-registered tools can be attached.</p>
                </div>
                <span className="rounded bg-soft px-2 py-1 text-[10px] font-bold text-ink-2">{draft.tools.length} selected</span>
              </div>
              <div className="grid grid-cols-2 gap-px bg-line">
                {toolOptions.map((tool) => {
                  const key = tool.function_tool_id || tool.actual_name || tool.name || "";
                  const selected = selectedToolKeys.has(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => updateDraft(
                        "tools",
                        selected
                          ? draft.tools.filter((item) => (item.function_tool_id || item.actual_name || item.name) !== key)
                          : [...draft.tools, {
                              function_tool_id: tool.function_tool_id || "",
                              name: tool.name || tool.actual_name || "",
                              actual_name: tool.actual_name || tool.name || "",
                              keyword: (tool.actual_name || tool.name || "") === "lookup_date"
                                ? DEFAULT_SCHEDULING_WINDOW
                                : "",
                            }],
                      )}
                      className={cn(
                        "min-h-28 bg-elevated p-4 text-left transition-colors hover:bg-soft",
                        selected && "bg-success-soft/40",
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-soft text-ink-2">
                          <Wrench className="h-4 w-4" />
                        </span>
                        <span className={cn(
                          "flex h-5 w-5 items-center justify-center rounded border",
                          selected ? "border-success bg-success text-white" : "border-line bg-elevated",
                        )}>
                          {selected && <Check className="h-3 w-3" />}
                        </span>
                      </div>
                      <p className="mt-3 text-xs font-bold text-ink">{tool.name || tool.actual_name}</p>
                      <p className="mt-1 line-clamp-2 text-[11px] leading-4 text-ink-3">{tool.description || tool.actual_name}</p>
                    </button>
                  );
                })}
              </div>
              {draft.tools.some((tool) => tool.actual_name === "search_nearby_places") && (
                <div className="border-t border-line bg-elevated p-5">
                  <label className="block max-w-xl">
                    <span className="mb-1.5 block text-xs font-bold text-ink">Nearby places search keyword</span>
                    <input
                      value={draft.tools.find((tool) => tool.actual_name === "search_nearby_places")?.keyword || ""}
                      onChange={(event) => updateDraft(
                        "tools",
                        draft.tools.map((tool) => (
                          tool.actual_name === "search_nearby_places"
                            ? { ...tool, keyword: event.target.value }
                            : tool
                        )),
                      )}
                      placeholder="hospital, pharmacy, ATM"
                      className="h-10 w-full rounded-md border border-line bg-soft px-3 text-sm outline-none focus:border-brand"
                    />
                  </label>
                </div>
              )}
              {draft.tools.some((tool) => tool.actual_name === "lookup_date") && (() => {
                const lookupTool = draft.tools.find((tool) => tool.actual_name === "lookup_date");
                const [windowStart, windowEnd] = splitSchedulingWindow(lookupTool?.keyword);
                const updateWindow = (startValue: string, endValue: string) => updateDraft(
                  "tools",
                  draft.tools.map((tool) => (
                    tool.actual_name === "lookup_date"
                      ? { ...tool, keyword: joinSchedulingWindow(startValue, endValue) }
                      : tool
                  )),
                );
                return (
                  <div className="border-t border-line bg-elevated p-5">
                    <label className="block max-w-xl">
                      <span className="mb-1.5 block text-xs font-bold text-ink">Scheduling window</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={windowStart}
                          onChange={(event) => updateWindow(event.target.value, windowEnd)}
                          className="h-10 w-36 rounded-md border border-line bg-soft px-3 text-sm outline-none focus:border-brand"
                        />
                        <span className="text-xs font-bold text-ink-3">to</span>
                        <input
                          type="time"
                          value={windowEnd}
                          onChange={(event) => updateWindow(windowStart, event.target.value)}
                          className="h-10 w-36 rounded-md border border-line bg-soft px-3 text-sm outline-none focus:border-brand"
                        />
                      </div>
                      <p className="mt-1.5 text-[11px] text-ink-3">
                        lookup_date only books demo/callback slots inside this window (e.g. 08:00 to 22:00).
                      </p>
                    </label>
                  </div>
                );
              })()}
            </div>
          )}

          {section === "reasoning" && isThinkerTalker && (
            <div className="grid min-h-full grid-rows-[76px_minmax(0,1fr)] gap-3">
              <div className="flex items-center gap-4 rounded-md border border-line bg-elevated px-5 shadow-sm">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand">
                  <BrainCircuit className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <h2 className="text-sm font-bold text-ink">Thinker-Talker routing</h2>
                  <p className="mt-0.5 text-[11px] text-ink-3">Classify each turn before response generation.</p>
                </div>
                <div className="ml-auto flex items-center gap-2 font-mono text-[10px] font-bold uppercase">
                  <span className="rounded bg-brand-soft px-2 py-1 text-brand">
                    {draft.thinkerCases.split("\n").filter((value) => value.trim()).length} Thinker
                  </span>
                  <span className="rounded bg-success-soft px-2 py-1 text-success">
                    {draft.talkerCases.split("\n").filter((value) => value.trim()).length} Talker
                  </span>
                </div>
              </div>
              <div className="grid min-h-0 grid-cols-2 gap-3">
                <section className="flex min-h-0 flex-col overflow-hidden rounded-md border border-line bg-elevated shadow-sm">
                  <div className="flex h-14 shrink-0 items-center justify-between border-b border-line px-5">
                    <div>
                      <h3 className="text-sm font-bold text-ink">Thinker routes</h3>
                      <p className="text-[10px] text-ink-3">Turns that require deliberate reasoning.</p>
                    </div>
                    <span className="rounded bg-brand-soft px-2 py-1 text-[10px] font-bold text-brand">Deep path</span>
                  </div>
                  <textarea
                    value={draft.thinkerCases}
                    onChange={(event) => updateDraft("thinkerCases", event.target.value)}
                    placeholder={"Pricing or EMI questions\nMedical or clinical questions\nDiscount bargaining\nEscalation requests"}
                    className="min-h-0 flex-1 resize-none border-0 bg-elevated p-5 font-mono text-xs leading-6 text-ink outline-none"
                  />
                  <div className="shrink-0 border-t border-line bg-soft px-5 py-2 font-mono text-[10px] text-ink-3">
                    One route per line
                  </div>
                </section>
                <section className="flex min-h-0 flex-col overflow-hidden rounded-md border border-line bg-elevated shadow-sm">
                  <div className="flex h-14 shrink-0 items-center justify-between border-b border-line px-5">
                    <div>
                      <h3 className="text-sm font-bold text-ink">Talker routes</h3>
                      <p className="text-[10px] text-ink-3">Turns answered immediately by the Talker.</p>
                    </div>
                    <span className="rounded bg-success-soft px-2 py-1 text-[10px] font-bold text-success">Fast path</span>
                  </div>
                  <textarea
                    value={draft.talkerCases}
                    onChange={(event) => updateDraft("talkerCases", event.target.value)}
                    placeholder={"General introduction\nSimple acknowledgements\nLanguage-switch requests\nAddress confirmation"}
                    className="min-h-0 flex-1 resize-none border-0 bg-elevated p-5 font-mono text-xs leading-6 text-ink outline-none"
                  />
                  <div className="shrink-0 border-t border-line bg-soft px-5 py-2 font-mono text-[10px] text-ink-3">
                    One route per line
                  </div>
                </section>
              </div>
            </div>
          )}

          {section === "call-data" && (
            <div className="grid min-h-full grid-cols-2 gap-3 bg-transparent">
              <section className="min-h-full rounded-md border border-line bg-elevated p-4 shadow-sm">
                <PostCallFieldEditor
                  label="Call infields"
                  items={draft.infields}
                  onChange={(items) => updateDraft("infields", items)}
                  maxDescriptionLength={1000}
                  infieldLanguages={infieldLanguageOptions}
                />
              </section>
              <section className="flex min-h-full flex-col gap-3 rounded-md border border-line bg-elevated p-4 shadow-sm">
                <OutcomeFindingsPanel
                  validation={outcomeGate.validation}
                  showOld={outcomeGate.showOld}
                  onShowOldChange={outcomeGate.setShowOld}
                  reviewing={outcomeGate.reviewing}
                  onReviewWithAi={() => void outcomeGate.reviewWithAi(outcomeCheckRequest, { advisoryOnly: isNew })}
                />
                <PostCallFieldEditor
                  findings={outcomeGate.validation?.mode === "off" ? [] : outcomeGate.validation?.findings}
                  showOldFindings={outcomeGate.showOld}
                  label="Call outcomes"
                  items={draft.outcomes}
                  onChange={(items) => updateDraft("outcomes", items)}
                  maxDescriptionLength={4000}
                  singleWinner={draft.outcomeSingleWinner}
                  onSingleWinnerChange={(next) => updateDraft("outcomeSingleWinner", next)}
                  primaryOutcome={draft.primaryOutcome}
                  onPrimaryOutcomeChange={(fieldName) => updateDraft("primaryOutcome", fieldName)}
                />
              </section>
            </div>
          )}

          {section === "runtime" && (
            <div className="min-h-full space-y-3 bg-transparent">
              <section className="rounded-md border border-line bg-elevated p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Gauge className="h-4 w-4 text-ink-3" />
                  <h2 className="text-sm font-bold text-ink">Speech runtime</h2>
                </div>
                <label className="mt-6 block">
                  <span className="mb-1.5 block text-xs font-bold text-ink">Primary transcription language</span>
                  <select
                    value={draft.sttLanguage}
                    onChange={(event) => {
                      const sttLanguage = event.target.value;
                      setDraft((current) => ({
                        ...current,
                        sttLanguage,
                        languagePreference: preferenceAfterSttChange(current.languagePreference, sttLanguage, current.languageSwitch),
                      }));
                    }}
                    className="h-10 w-full rounded-md border border-line bg-elevated px-3 text-sm outline-none focus:border-brand"
                  >
                    {STT_LANGUAGE_GROUPS.map((group: { label: string; options: Array<{ value: string; label: string }> }) => (
                      <optgroup key={group.label} label={group.label}>
                        {group.options.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </label>
                <label className="mt-5 flex items-center justify-between rounded-md border border-line bg-soft px-4 py-3">
                  <span>
                    <span className="block text-xs font-bold text-ink">Mid-call language switching</span>
                    <span className="mt-0.5 block text-[10px] text-ink-3">Allow the reply language to follow the caller.</span>
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={draft.languageSwitch}
                    onClick={() => setDraft((current) => {
                      const languageSwitch = !current.languageSwitch;
                      // Turning switching on for a version with no pair yet starts from the native-first default.
                      const languagePreference = languageSwitch && !current.languagePreference.primaryLanguage
                        ? defaultLanguagePreference(current.sttLanguage)
                        : current.languagePreference;
                      return { ...current, languageSwitch, languagePreference };
                    })}
                    className={cn("relative h-5 w-9 rounded-full", draft.languageSwitch ? "bg-brand" : "bg-line")}
                  >
                    <span className={cn(
                      "absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all",
                      draft.languageSwitch ? "left-[18px]" : "left-0.5",
                    )} />
                  </button>
                </label>
                {draft.languageSwitch && (
                  <LanguagePreferenceFields
                    value={draft.languagePreference}
                    sttLanguage={draft.sttLanguage}
                    onChange={(languagePreference) => updateDraft("languagePreference", languagePreference)}
                  />
                )}
                <div className="mt-6 border-t border-line pt-5">
                  <p className="text-xs font-bold text-ink">Current version</p>
                  <dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
                    <div><dt className="text-ink-3">Version</dt><dd className="mt-1 font-mono font-bold text-ink">{draft.versionName}</dd></div>
                    <div><dt className="text-ink-3">Version ID</dt><dd className="mt-1 truncate font-mono font-bold text-ink" title={activeVersionId}>{activeVersionId || "Pending publish"}</dd></div>
                    <div><dt className="text-ink-3">Status</dt><dd className="mt-1 font-bold text-ink">{isNew ? "Unpublished" : agent.status || "Draft"}</dd></div>
                  </dl>
                </div>
              </section>
              <section className="rounded-md border border-line bg-elevated p-4 shadow-sm">
                <TransferEditor
                  value={draft.transfer}
                  onChange={(transfer) => updateDraft("transfer", transfer)}
                />
              </section>
              {canAccessKnowledge(user) && (
                <section className="rounded-md border border-line bg-elevated p-4 shadow-sm">
                  <KnowledgeEditor agentId={isNew ? null : agentId} saved={agent?.knowledge ?? null} />
                </section>
              )}
            </div>
          )}

          {section === "versions" && (
            <div className="min-h-full rounded-md border border-line bg-elevated p-5 shadow-sm">
              <VersionHistory
                versions={versions}
                onLoadDetails={(versionId: string) => core<AgentVersion>(
                  `/api/v1/agents/${encodeURIComponent(agentId)}/versions/${encodeURIComponent(versionId)}`,
                )}
                onEdit={editVersion}
                onDelete={(version) => void deleteVersion(version)}
                onCreate={isNew ? undefined : createVersion}
                onRefresh={isNew ? undefined : refreshVersions}
                loading={versionsLoading}
              />
            </div>
          )}
        </main>
        </Panel>
        
        <PanelResizeHandle className="relative flex w-3 shrink-0 items-center justify-center group outline-none">
          <div className="h-8 w-1 rounded-full bg-line/50 group-hover:bg-brand/50 transition-colors" />
        </PanelResizeHandle>

        <Panel defaultSize={20} minSize={15} className={cn("flex flex-col min-h-0", compactStudio && scoutOpen && "hidden")}>
          <aside className="flex h-full min-h-0 flex-col overflow-hidden rounded-md border border-line bg-elevated shadow-sm">
            <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-4">
              <Settings2 className="h-4 w-4 text-ink-3" />
              <h2 className="text-xs font-bold text-ink">Agent configuration</h2>
            </div>
            <div className="border-b border-line p-4">
              <label className="block">
                <span className="mb-1 block font-mono text-[10px] font-bold uppercase text-ink-3">Description</span>
                <textarea
                  rows={3}
                  maxLength={150}
                  value={draft.description}
                  onChange={(event) => updateDraft("description", event.target.value)}
                  placeholder="What this agent owns"
                  className="w-full resize-none rounded-md border border-line bg-soft px-2.5 py-2 text-xs leading-5 outline-none focus:border-brand"
                />
              </label>
            </div>
            <nav className="flex-1 divide-y divide-line overflow-y-auto px-3">
              {sectionItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => changeSection(item.id)}
                    className={cn(
                      "flex min-h-14 w-full items-center gap-3 px-2 text-left text-xs font-bold",
                      section === item.id ? "text-brand" : "text-ink-2 hover:text-ink",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                    <ChevronRight className="ml-auto h-3.5 w-3.5 opacity-50" />
                  </button>
                );
              })}
            </nav>
          </aside>
        </Panel>

        {!(normalPromptExpanded && section === "prompt") && (
          <>
            <PanelResizeHandle className="relative flex w-3 shrink-0 items-center justify-center group outline-none">
              <div className="h-8 w-1 rounded-full bg-line/50 group-hover:bg-brand/50 transition-colors" />
            </PanelResizeHandle>

            <Panel defaultSize={30} minSize={20} className={cn("flex flex-col min-h-0", scoutOpen && "hidden")}>
              <aside className="h-full min-h-0 space-y-3 overflow-y-auto bg-transparent">
                <AgentTestWorkspace
                  allowTranscriptExpand
                  agentId={agent.agent_id}
                  agentName={draft.agentName}
                  architecture={architecture}
                  activeVersionId={activeVersionId}
                  versions={versions}
                  isNew={isNew}
                  dirty={dirty}
                  ensureVersions={ensureVersions}
                  loadVersionDetails={(versionId) => core<AgentVersion>(
                    `/api/v1/agents/${encodeURIComponent(agentId)}/versions/${encodeURIComponent(versionId)}`,
                  )}
                />
                <div className="rounded-md border border-line bg-elevated p-5 shadow-sm">
                  <h2 className="text-xs font-bold uppercase text-ink-2">Configuration</h2>
                  <dl className="mt-4 space-y-3 text-xs">
                    <div className="flex justify-between gap-3"><dt className="text-ink-3">Tools</dt><dd className="font-mono font-bold text-ink">{draft.tools.length}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-ink-3">Inputs</dt><dd className="font-mono font-bold text-ink">{draft.infields.length}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-ink-3">Outcomes</dt><dd className="font-mono font-bold text-ink">{draft.outcomes.length}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-ink-3">Language</dt><dd className="font-mono font-bold uppercase text-ink">{draft.sttLanguage}</dd></div>
                  </dl>
                </div>
              </aside>
            </Panel>
          </>
        )}

        {scoutOpen && (
          <>
            <PanelResizeHandle className="mx-1 w-1.5 cursor-col-resize rounded-full bg-line hover:bg-brand/40 transition-colors" />
            <Panel defaultSize={35} minSize={25} className="flex flex-col min-h-0 overflow-hidden rounded-xl border border-line bg-canvas shadow-sm" style={compactStudio ? { flexGrow: 100 } : undefined}>
              <div className="flex h-10 shrink-0 items-center gap-2 border-b border-line bg-elevated px-4">
                <Navigation className="h-3.5 w-3.5 text-brand" />
                <span className="min-w-0 flex-1 truncate text-xs font-bold text-ink">Scout <span className="font-normal text-ink-3">· {draft.agentName}</span></span>
                <button
                  onClick={() => setScoutOpen(false)}
                  className="ml-auto rounded p-1 text-ink-3 hover:bg-soft hover:text-ink"
                  aria-label="Close Scout"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </button>
              </div>
              <ScoutBuilderPane conversationId={scoutConversationId || undefined} agentId={agentId || undefined} context={scoutContext} onApplyDraft={handleApplyScoutDraft} onUndo={undoScout} canUndo={Boolean(beforeScout)} onConversationChange={handleScoutConversation} />
            </Panel>
          </>
        )}
      </PanelGroup>
      )}
      {outcomeGate.modal}
      {publishDialogOpen && (
        <PublishVersionModal
          entityLabel="agent"
          mode={publishMode}
          onModeChange={setPublishMode}
          newVersionName={newVersionName}
          onNewVersionNameChange={setNewVersionName}
          overwriteVersionName={overwriteVersionName}
          onOverwriteVersionNameChange={setOverwriteVersionName}
          canOverwrite={!isNew && Boolean(activeVersionId) && !hasPendingSchedule}
          overwriteDisabledReason={
            hasPendingSchedule
              ? "A scheduled call is using this version. Create a new version instead."
              : "Available after the first version has been published."
          }
          publishing={publishing}
          onClose={() => setPublishDialogOpen(false)}
          onPublish={(mode, versionName) => void publish(mode, versionName)}
        />
      )}
    </div>
  );
}
